import { useReducer } from 'react'

/**
 * State interface for the Integrations component
 */
interface IntegrationsState {
  // DaySmart integration state
  acceptedTerms: boolean
  isLoading: boolean
  isLoadingTest: boolean
  testSuccess: boolean
  testSubmitted: boolean
  apiKey: string
  apiKeyError: string
  secretKey: string
  secretKeyError: string
  error: string

  // USA Hockey integration state
  hockeyUSAApiKey: string
  hockeyUSASecretKey: string
  isHockeyUSAConnected: boolean
  isHockeyUSAEnabled: boolean
  showHockeyUSAWarning: boolean
  pendingHockeyUSAEnabled: boolean
  /** SN-8202. Last accepted day for a previous-season number, 'MM-DD'; null = USA Hockey's own. */
  hockeyUSAPreviousSeasonCutoff: string | null
  isSavingHockeyUSASeasonCutoff: boolean
}

/**
 * Action types for the integrations reducer
 */
type IntegrationsAction =
  // DaySmart actions
  | { type: 'SET_ACCEPTED_TERMS'; payload: boolean }
  | { type: 'SET_IS_LOADING'; payload: boolean }
  | { type: 'SET_IS_LOADING_TEST'; payload: boolean }
  | { type: 'SET_TEST_SUCCESS'; payload: boolean }
  | { type: 'SET_TEST_SUBMITTED'; payload: boolean }
  | { type: 'SET_API_KEY'; payload: string }
  | { type: 'SET_API_KEY_ERROR'; payload: string }
  | { type: 'SET_SECRET_KEY'; payload: string }
  | { type: 'SET_SECRET_KEY_ERROR'; payload: string }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'RESET_TEST_STATE' }
  | { type: 'CLEAR_ERRORS' }

  // USA Hockey actions
  | { type: 'SET_HOCKEY_USA_API_KEY'; payload: string }
  | { type: 'SET_HOCKEY_USA_SECRET_KEY'; payload: string }
  | { type: 'SET_IS_HOCKEY_USA_CONNECTED'; payload: boolean }
  | { type: 'SET_IS_HOCKEY_USA_ENABLED'; payload: boolean }
  | { type: 'SET_SHOW_HOCKEY_USA_WARNING'; payload: boolean }
  | { type: 'SET_PENDING_HOCKEY_USA_ENABLED'; payload: boolean }
  | { type: 'RESET_HOCKEY_USA_WARNING_STATE' }
  | { type: 'SET_HOCKEY_USA_PREVIOUS_SEASON_CUTOFF'; payload: string | null }
  | { type: 'SET_IS_SAVING_HOCKEY_USA_SEASON_CUTOFF'; payload: boolean }

  // Combined actions for common patterns
  | {
      type: 'SET_DAYSMART_KEYS'
      payload: { apiKey: string; secretKey: string }
    }
  | {
      type: 'SET_HOCKEY_USA_DATA'
      payload: { apiKey: string; secretKey: string; enabled: boolean }
    }
  | {
      type: 'SET_FIELD_ERRORS'
      payload: { apiKeyError?: string; secretKeyError?: string }
    }

/**
 * Initial state for the integrations reducer
 */
const initialState: IntegrationsState = {
  // DaySmart integration state
  acceptedTerms: false,
  isLoading: false,
  isLoadingTest: false,
  testSuccess: false,
  testSubmitted: false,
  apiKey: '',
  apiKeyError: '',
  secretKey: '',
  secretKeyError: '',
  error: '',

  // USA Hockey integration state
  hockeyUSAApiKey: '',
  hockeyUSASecretKey: '',
  isHockeyUSAConnected: false,
  isHockeyUSAEnabled: true,
  showHockeyUSAWarning: false,
  pendingHockeyUSAEnabled: false,
  hockeyUSAPreviousSeasonCutoff: null,
  isSavingHockeyUSASeasonCutoff: false,
}

/**
 * Reducer function for managing integrations state
 * @param state - Current state
 * @param action - Action to be processed
 * @returns Updated state
 */
const integrationsReducer = (
  state: IntegrationsState,
  action: IntegrationsAction
): IntegrationsState => {
  console.log('🔄 integrationsReducer: Processing action', {
    type: action.type,
    payload: 'payload' in action ? action.payload : undefined,
  })

  switch (action.type) {
    // DaySmart actions
    case 'SET_ACCEPTED_TERMS':
      return { ...state, acceptedTerms: action.payload }

    case 'SET_IS_LOADING':
      return { ...state, isLoading: action.payload }

    case 'SET_IS_LOADING_TEST':
      return { ...state, isLoadingTest: action.payload }

    case 'SET_TEST_SUCCESS':
      return { ...state, testSuccess: action.payload }

    case 'SET_TEST_SUBMITTED':
      return { ...state, testSubmitted: action.payload }

    case 'SET_API_KEY':
      return { ...state, apiKey: action.payload }

    case 'SET_API_KEY_ERROR':
      return { ...state, apiKeyError: action.payload }

    case 'SET_SECRET_KEY':
      return { ...state, secretKey: action.payload }

    case 'SET_SECRET_KEY_ERROR':
      return { ...state, secretKeyError: action.payload }

    case 'SET_ERROR':
      return { ...state, error: action.payload }

    case 'RESET_TEST_STATE':
      return {
        ...state,
        testSuccess: false,
        testSubmitted: false,
      }

    case 'CLEAR_ERRORS':
      return {
        ...state,
        error: '',
        apiKeyError: '',
        secretKeyError: '',
      }

    // USA Hockey actions
    case 'SET_HOCKEY_USA_API_KEY':
      return { ...state, hockeyUSAApiKey: action.payload }

    case 'SET_HOCKEY_USA_SECRET_KEY':
      return { ...state, hockeyUSASecretKey: action.payload }

    case 'SET_IS_HOCKEY_USA_CONNECTED':
      return { ...state, isHockeyUSAConnected: action.payload }

    case 'SET_IS_HOCKEY_USA_ENABLED':
      return { ...state, isHockeyUSAEnabled: action.payload }

    case 'SET_SHOW_HOCKEY_USA_WARNING':
      return { ...state, showHockeyUSAWarning: action.payload }

    case 'SET_PENDING_HOCKEY_USA_ENABLED':
      return { ...state, pendingHockeyUSAEnabled: action.payload }

    case 'RESET_HOCKEY_USA_WARNING_STATE':
      return {
        ...state,
        showHockeyUSAWarning: false,
        pendingHockeyUSAEnabled: false,
      }

    case 'SET_HOCKEY_USA_PREVIOUS_SEASON_CUTOFF':
      return { ...state, hockeyUSAPreviousSeasonCutoff: action.payload }

    case 'SET_IS_SAVING_HOCKEY_USA_SEASON_CUTOFF':
      return { ...state, isSavingHockeyUSASeasonCutoff: action.payload }

    // Combined actions for common patterns
    case 'SET_DAYSMART_KEYS':
      return {
        ...state,
        apiKey: action.payload.apiKey,
        secretKey: action.payload.secretKey,
        acceptedTerms: true,
      }

    case 'SET_HOCKEY_USA_DATA':
      return {
        ...state,
        hockeyUSAApiKey: action.payload.apiKey,
        hockeyUSASecretKey: action.payload.secretKey,
        isHockeyUSAConnected: true,
        isHockeyUSAEnabled: action.payload.enabled,
      }

    case 'SET_FIELD_ERRORS':
      return {
        ...state,
        apiKeyError: action.payload.apiKeyError || state.apiKeyError,
        secretKeyError: action.payload.secretKeyError || state.secretKeyError,
      }

    default:
      console.warn('⚠️ integrationsReducer: Unknown action type', action)
      return state
  }
}

/**
 * Custom hook for managing integrations state with useReducer
 *
 * This hook provides a centralized state management solution for the Integrations component,
 * replacing multiple useState hooks with a single useReducer pattern for better maintainability
 * and predictable state updates.
 *
 * @returns Object containing current state and dispatch function with action creators
 *
 * @example
 * ```typescript
 * const { state, actions } = useIntegrationsReducer()
 *
 * // Update individual state values
 * actions.setAcceptedTerms(true)
 * actions.setIsLoading(false)
 *
 * // Update multiple related values at once
 * actions.setDaySmartKeys({ apiKey: 'key123', secretKey: 'secret456' })
 *
 * // Access current state
 * console.log(state.acceptedTerms, state.apiKey)
 * ```
 */
export const useIntegrationsReducer = () => {
  console.log('🔄 useIntegrationsReducer: Initializing reducer hook')

  const [state, dispatch] = useReducer(integrationsReducer, initialState)

  /**
   * Action creators for easier state updates
   * These functions provide a clean API for dispatching actions without requiring
   * components to know the exact action structure
   */
  const actions = {
    // DaySmart action creators
    setAcceptedTerms: (acceptedTerms: boolean) =>
      dispatch({ type: 'SET_ACCEPTED_TERMS', payload: acceptedTerms }),

    setIsLoading: (isLoading: boolean) =>
      dispatch({ type: 'SET_IS_LOADING', payload: isLoading }),

    setIsLoadingTest: (isLoadingTest: boolean) =>
      dispatch({ type: 'SET_IS_LOADING_TEST', payload: isLoadingTest }),

    setTestSuccess: (testSuccess: boolean) =>
      dispatch({ type: 'SET_TEST_SUCCESS', payload: testSuccess }),

    setTestSubmitted: (testSubmitted: boolean) =>
      dispatch({ type: 'SET_TEST_SUBMITTED', payload: testSubmitted }),

    setApiKey: (apiKey: string) =>
      dispatch({ type: 'SET_API_KEY', payload: apiKey }),

    setApiKeyError: (apiKeyError: string) =>
      dispatch({ type: 'SET_API_KEY_ERROR', payload: apiKeyError }),

    setSecretKey: (secretKey: string) =>
      dispatch({ type: 'SET_SECRET_KEY', payload: secretKey }),

    setSecretKeyError: (secretKeyError: string) =>
      dispatch({ type: 'SET_SECRET_KEY_ERROR', payload: secretKeyError }),

    setError: (error: string) =>
      dispatch({ type: 'SET_ERROR', payload: error }),

    resetTestState: () => dispatch({ type: 'RESET_TEST_STATE' }),

    clearErrors: () => dispatch({ type: 'CLEAR_ERRORS' }),

    // USA Hockey action creators
    setHockeyUSAApiKey: (hockeyUSAApiKey: string) =>
      dispatch({ type: 'SET_HOCKEY_USA_API_KEY', payload: hockeyUSAApiKey }),

    setHockeyUSASecretKey: (hockeyUSASecretKey: string) =>
      dispatch({
        type: 'SET_HOCKEY_USA_SECRET_KEY',
        payload: hockeyUSASecretKey,
      }),

    setIsHockeyUSAConnected: (isHockeyUSAConnected: boolean) =>
      dispatch({
        type: 'SET_IS_HOCKEY_USA_CONNECTED',
        payload: isHockeyUSAConnected,
      }),

    setIsHockeyUSAEnabled: (isHockeyUSAEnabled: boolean) =>
      dispatch({
        type: 'SET_IS_HOCKEY_USA_ENABLED',
        payload: isHockeyUSAEnabled,
      }),

    setShowHockeyUSAWarning: (showHockeyUSAWarning: boolean) =>
      dispatch({
        type: 'SET_SHOW_HOCKEY_USA_WARNING',
        payload: showHockeyUSAWarning,
      }),

    setPendingHockeyUSAEnabled: (pendingHockeyUSAEnabled: boolean) =>
      dispatch({
        type: 'SET_PENDING_HOCKEY_USA_ENABLED',
        payload: pendingHockeyUSAEnabled,
      }),

    resetHockeyUSAWarningState: () =>
      dispatch({ type: 'RESET_HOCKEY_USA_WARNING_STATE' }),

    setHockeyUSAPreviousSeasonCutoff: (cutoff: string | null) =>
      dispatch({
        type: 'SET_HOCKEY_USA_PREVIOUS_SEASON_CUTOFF',
        payload: cutoff,
      }),

    setIsSavingHockeyUSASeasonCutoff: (isSaving: boolean) =>
      dispatch({
        type: 'SET_IS_SAVING_HOCKEY_USA_SEASON_CUTOFF',
        payload: isSaving,
      }),

    // Combined action creators for common patterns
    setDaySmartKeys: (apiKey: string, secretKey: string) =>
      dispatch({ type: 'SET_DAYSMART_KEYS', payload: { apiKey, secretKey } }),

    setHockeyUSAData: (apiKey: string, secretKey: string, enabled: boolean) =>
      dispatch({
        type: 'SET_HOCKEY_USA_DATA',
        payload: { apiKey, secretKey, enabled },
      }),

    setFieldErrors: (errors: {
      apiKeyError?: string
      secretKeyError?: string
    }) => dispatch({ type: 'SET_FIELD_ERRORS', payload: errors }),
  }

  return {
    state,
    actions,
  }
}
